<template>
  <div class="relative max-w-xl px-6 py-6 mx-auto text-dark-type-4">
    <div>
      <div class="inline-flex items-center">
        <button :disabled="processing" @click="onClickBackButton">
          <img
            src="@/assets/img/back-button.svg"
            class="h-auto mr-4 md:w-auto md:h-auto"
            alt="Back button"
          />
        </button>
        <div class="font-bold text-center">Booking breakdown</div>
      </div>
    </div>
    <div class="mt-12">
      <div class="flex items-center justify-between mt-6">
        <div class="text-xs font-medium text-dark-type-4">Ticket Type</div>
        <div class="text-xs font-medium text-dark-type-3">
          {{ paymentInformation.ticketType.split("-").join(" ") | titleCase }}
        </div>
      </div>
      <div class="flex items-center justify-between mt-4">
        <div class="text-xs font-medium text-dark-type-4">Passengers</div>
        <div class="text-xs font-medium text-dark-type-3">
          {{ paymentInformation.passengers }}
        </div>
      </div>
      <div class="flex items-center justify-between mt-4">
        <div class="text-xs font-medium text-dark-type-4">
          Price per Passenger
          {{
            paymentInformation.ticketType.toLowerCase() === "return"
              ? "(Outbound)"
              : ""
          }}
        </div>
        <div class="text-xs font-medium text-dark-type-3">
          &#8358; {{ paymentInformation.pricePerPassenger }}
        </div>
      </div>
      <div
        v-if="paymentInformation.ticketType.toLowerCase() === 'return'"
        class="flex items-center justify-between mt-4"
      >
        <div class="text-xs font-medium text-dark-type-4">
          Price per Passenger (Return)
        </div>
        <div class="text-xs font-medium text-dark-type-3">
          &#8358; {{ paymentInformation.returnTripPricePerPassenger }}
        </div>
      </div>
      <div class="flex items-center justify-between mt-4">
        <div class="text-xs font-medium text-dark-type-4">Luggage Cost</div>
        <div class="text-xs font-medium text-dark-type-3">
          &#8358; {{ paymentInformation.luggageCost }}
        </div>
      </div>
      <div class="flex items-center justify-between mt-4">
        <div class="text-xs font-medium text-dark-type-4">Payment Method</div>
        <div class="text-xs font-medium text-dark-type-3">
          {{
            paymentInformation.paymentOption.split("_").join(" ") | titleCase
          }}
        </div>
      </div>
      <template v-if="isWalletPayment">
        <div class="flex items-center justify-between mt-4">
          <div class="text-xs font-medium text-dark-type-4">Wallet Source</div>
          <div class="text-xs font-medium text-dark-type-3">
            {{
              paymentInformation.walletSource === "main_balance"
                ? "Personal"
                : "Corporate"
            }}
          </div>
        </div>
        <div class="flex items-center justify-between mt-4">
          <div class="text-xs font-bold text-dark-type-3">Wallet Balance</div>
          <div class="text-xs font-bold text-dark-type-3">
            &#8358;
            {{
              paymentInformation.walletSource &&
              paymentInformation.walletSource === "credit_balance"
                ? wallet.credit_amount
                : wallet.main_balance | currency
            }}
          </div>
        </div>
      </template>
      <div class="flex items-center justify-between mt-4">
        <div class="text-xs font-bold text-dark-type-3">Total</div>
        <div class="text-xs font-bold text-dark-type-3">
          &#8358; {{ paymentInformation.total }}
        </div>
      </div>
    </div>
    <div
      class="px-4 py-4 mt-10 text-white rounded bg-red-type-1"
      v-if="isWalletPayment & walletHasInsufficientBalance"
    >
      <div class="text-xs font-bold">Insufficient balance</div>
      <div class="mt-2 text-xs">
        Your wallet balance is insufficient for this booking. Fund to continue
      </div>
    </div>
    <div class="py-2 mt-4" v-if="paymentInformation.discountText">
      <div class="">
        <p class="text-xs font-medium text-active italic">
          {{ paymentInformation.discountText }}
        </p>
      </div>
    </div>
    <button
      @click="book"
      v-if="
        isInstantPayment || (isWalletPayment && !walletHasInsufficientBalance)
      "
      class="
        block
        w-full
        h-12
        py-4
        mt-8
        font-bold
        text-center text-white
        rounded
        focus:outline-none
      "
      :class="processing ? 'bg-gray-type-7' : 'bg-active'"
    >
      {{
        processing
          ? "Processing"
          : `Pay &#8358; ${this.paymentInformation.payableAmount}`
      }}
    </button>
    <router-link
      v-else
      :to="{ name: 'dashboard.wallet' }"
      class="
        block
        w-full
        h-12
        py-4
        mt-8
        font-bold
        text-center text-white
        rounded
        bg-active
      "
      >Fund Wallet</router-link
    >
  </div>
</template>


<script>
import { mapGetters } from "vuex";
import paymentProcessor from "@/mixins/payment-processor";
import axios from "@/services/axios";
import {ValidationException} from "@/models/validation-exception";

export default {
  props: {
    paymentInformation: Object,
  },
  components: {},
  mixins: [paymentProcessor],
  data() {
    return {
      processing: false,
      paymentReference: null,
      bookingFailed: false,
      bookingResponse: null,
      instantPaymentTimer: {
        limit: 270 * 1000, // in seconds (4minutes 30 seconds)
        timerRef: null
      },
      payStackHandler: null
    };
  },
  watch: {
    show(value) {
      if (!value) {
        this.maybeDeleteBookingWithReference();
      }
    },
  },
  computed: {
    ...mapGetters("app", ["wallet"]),
    ...mapGetters("auth", ["user"]),
    walletHasInsufficientBalance() {
      if (!this.isWalletPayment) {
        return false;
      }

      const walletSources = ["credit_balance", "main_balance"];

      if (!walletSources.includes(this.paymentInformation.walletSource)) {
        return true;
      }
      if (this.paymentInformation.walletSource === "credit_balance") {
        return (
          parseFloat(this.wallet.credit_amount) <
          parseFloat(this.paymentInformation.total)
        );
      } else {
        return (
          parseFloat(this.wallet.main_balance) <
          parseFloat(this.paymentInformation.total)
        );
      }
    },
    isWalletPayment() {
      return this.paymentInformation.paymentOption === "wallet";
    },
    isInstantPayment() {
      return this.paymentInformation.paymentOption === "instant_payment";
    },
    isFreeRoute() {
      return this.paymentInformation.pricePerPassenger === 0;
    },
  },
  methods: {
    cleanupPaymentState() {
      const isFunctionCallable = (obj, name) => {
        return obj && obj[name] && typeof obj[name] === 'function';
      }
      // Close paystack iframe if still active
      if(this.payStackHandler && this.payStackHandler?.isIframeOpen) {
        try {
          if(isFunctionCallable(this.payStackHandler, 'closeNewCheckout')) {
            this.payStackHandler.closeNewCheckout();
          } else if(isFunctionCallable(this.payStackHandler, 'closeOldCheckout')) {
            this.payStackHandler.closeOldCheckout();
          }
          console.info('Payment state reset');
        } catch (e) {
          console.info('Failed to clear payment state');
        }
      }
    },
    async book() {
      if (this.processing) {
        return;
      }

      try {
        this.processing = true;

        this.bookingFailed = false;

        if (this.isWalletPayment) {
          const response = await this.processBooking();

          this.$store.dispatch("app/fetchWallet");

          this.processing = false;

          this.markBookingSuccessful(response);

          return;
        }

        if (this.isInstantPayment) {
          if (!this.paymentReference) {
            this.paymentReference =
              "" + Math.floor(Math.random() * 1000000000 + 1);
          }

          this.bookingResponse = await this.processBooking({
            paymentSource: "instant_payment",
            paymentReference: this.paymentReference,
          });

          let totalAmount = 0;
          // Booking response from the Dashboard file is an array,
          // while Booking response from the NewDashboard file is an object.
          // This logic handles the difference.
          if (Array.isArray(this.bookingResponse)) {
            this.bookingResponse.forEach((response) => {
              totalAmount += response.cost;
            });
          } else {
            totalAmount = this.bookingResponse.data.cost;
          }
          // callback called when inline frame opens
          const initCallback = (options) => {
            this.payStackHandler = options.handler;
          };

          this.instantPaymentTimer.timerRef = setTimeout(() => {
            this.$toast.error('Please try again', 'Booking timed out.', {
              closeButton: true,
              timeOut: 0,
              extendedTimeOut: 0
            });
            this.close();
            this.cleanupPaymentState();
          }, this.instantPaymentTimer.limit);

          this.pay({
            amount: totalAmount,
            user_id: this.user.id,
            customer_email: this.user.email,
            ref: this.paymentReference,
            meta_data: {
              payment_source: "instant_payment",
            },
          }, initCallback)
            .then(async () => {
              await axios.post(
                `/instant-payment/requery/${this.paymentReference}`
              );
              this.markBookingSuccessful(this.bookingResponse);
            })
            .catch((error) => {
              this.processBookingError(error);

              this.bookingFailed = true;
            })
            .finally(() => {
              this.processing = false;
              clearTimeout(this.instantPaymentTimer.timerRef);
            });
        }
      } catch (error) {
        this.processing = false;
        this.processBookingError(error);
      }
    },
    async processBooking(data = null) {
      return this.paymentInformation.processBooking(data);
    },
    markBookingSuccessful(response) {
      this.$logEvent("booking_payment_successful", {
        ...response,
        ...this.paymentInformation,
      });

      this.resetBookingStates();

      this.$emit("payment-success", response);
    },
    processBookingError(error = null) {
      this.$emit("payment-failed", error);
      if (error && error.response &&
        Object.prototype.hasOwnProperty.call(error.response.data, "message")) {
        this.$toast.error(error.response.data.message);
      } else if(error instanceof ValidationException) {
        this.$toast.error(error.message);
      }
      this.$toast.error("Payment failed, please try again.");
      this.close();
    },
    maybeDeleteBookingWithReference() {
      if (!this.paymentReference || !this.bookingFailed) {
        return;
      }
      this.$http
        .delete(`/instant-payment/${this.paymentReference}`)
        .then(() => {
          this.paymentReference = null;
          this.resetBookingStates();
        });
    },
    resetBookingStates() {
      this.paymentReference = null;
      this.bookingResponse = null;
      this.bookingFailed = false;
    },
    onClickBackButton() {
      if (this.isFreeRoute) {
        this.$emit("close");
      } else {
        this.$emit("back");
      }
    },
    close() {
      this.$emit("close");
    },
  },
  beforeDestroy() {
    this.cleanupPaymentState();
  }
};
</script>
